import { onShow, onHide } from '@intercom/messenger-js-sdk';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { authentication } from '@perpay-web/authentication/singleton';
import { getIsUnrequested } from '@perpay-web/data-module/useDataModule';
import {
    useFetchDecagonSignatureDataModuleContext,
    getIsEnabled as getIsDecagonEnabled,
} from '@perpay-web/hooks/useFetchDecagonSignatureDataModuleContext';
import { useIsSemiLarge } from '@perpay-web/hooks/useIsSemiLarge';
import { getIsReactNative } from '@perpay-web/native-client/getIsReactNative';
import { useNativeSelector } from '@perpay-web/native-client/hooks/useNativeSelector';
import { getAppVersion } from '@perpay-web/native-client/selectors/appInfo';
import { loadDecagonSupportTool } from '@perpay-web/services/decagon';
import {
    launchSupportTool,
    closeSupportTool,
    updateSupportTool,
    analyticsSelfHelpOpened,
} from '@perpay-web/services/supportTool';

const ICON_PADDING = 20;
const FOOTER_PADDING = 60;
export const useSupportTool = (repositionSupportTool = false) => {
    const dispatch = useDispatch();
    const isSubscribedRef = useRef(false);
    const { isSemiLarge } = useIsSemiLarge();

    const updateSupportToolHandler = useCallback(
        (payload) => dispatch(updateSupportTool(payload)),
        [dispatch],
    );
    const supportToolLaunched = useCallback(() => {
        dispatch(launchSupportTool());
        dispatch(analyticsSelfHelpOpened());
    }, [dispatch]);
    const supportToolClosed = useCallback(
        () => dispatch(closeSupportTool()),
        [dispatch],
    );

    const { dataRequest: fetchDecagonSignature, state: decagonSignatureState } =
        useFetchDecagonSignatureDataModuleContext();
    const isDecagonUnrequested = getIsUnrequested(decagonSignatureState);

    useEffect(() => {
        if (isDecagonUnrequested) {
            fetchDecagonSignature();
        }
    }, [isDecagonUnrequested, dispatch, fetchDecagonSignature]);

    const isDecagonEnabled = getIsDecagonEnabled(decagonSignatureState);
    const nativeAppVersion = useNativeSelector(getAppVersion);
    const showDecagon =
        isDecagonEnabled && (!getIsReactNative() || nativeAppVersion);

    useEffect(() => {
        if (showDecagon && !window.duet) {
            dispatch(loadDecagonSupportTool({ hideLauncher: !isSemiLarge }));
        }
    }, [dispatch, showDecagon, isSemiLarge]);

    // make support tool shift up if it intersects with storefront footer
    useEffect(() => {
        updateSupportToolHandler({
            vertical_padding:
                ICON_PADDING + (repositionSupportTool ? FOOTER_PADDING : 0),
        });
    }, [repositionSupportTool, updateSupportToolHandler]);

    // show/hide support tool based on screen size
    useEffect(() => {
        updateSupportToolHandler({
            hide_default_launcher: !isSemiLarge,
        });
    }, [isSemiLarge, updateSupportToolHandler]);

    // Subscribe to support tool events
    const isAuthenticated = authentication.getIsAuthenticated();
    useEffect(() => {
        // subscribe to listeners only after user is authenticated
        if (!isSubscribedRef.current && isAuthenticated && !showDecagon) {
            onShow(() => {
                supportToolLaunched();
            });
            onHide(() => {
                supportToolClosed();
            });
            isSubscribedRef.current = true;
        }
    }, [supportToolLaunched, supportToolClosed, isAuthenticated, showDecagon]);
};
